/**
 * Local-calendar EMI due dates from loan opening date.
 * Avoid `toISOString().slice(0,10)` — it shifts dates in IST and breaks backdated schedules.
 */

/** Format a Date as YYYY-MM-DD in local calendar. */
export function formatLocalYmd(d: Date): string {
  if (!Number.isFinite(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function parseDateOnly(iso: string): string {
  if (!iso) return '';
  const s = String(iso).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return formatLocalYmd(d);
}

/** Parse start/opening date to a stable local noon Date. */
export function parseLoanStartDate(startIso: string): Date | null {
  const ymd = parseDateOnly(startIso);
  if (!ymd) {
    const d = new Date(startIso);
    return Number.isFinite(d.getTime()) ? d : null;
  }
  const d = new Date(`${ymd}T12:00:00`);
  return Number.isFinite(d.getTime()) ? d : null;
}

/** Add calendar months, clamping to the target month's last day (31 Jan + 1 → 28/29 Feb). */
export function addMonthsClamped(date: Date, months: number): Date {
  const day = date.getDate();
  const d = new Date(date);
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, lastDay));
  return d;
}

/**
 * Due date for installment slot (0-based). Slot 0 = first EMI = opening date + 1 calendar month.
 */
export function emiDueDateYmd(startIso: string, slotIndex: number): string {
  const start = parseLoanStartDate(startIso);
  if (!start) return '';
  return formatLocalYmd(addMonthsClamped(start, slotIndex + 1));
}

/** End-of-day timestamp for due-date comparisons in replay. */
export function emiDueTimeMs(startIso: string, monthIndex1Based: number): number {
  const ymd = emiDueDateYmd(startIso, monthIndex1Based - 1);
  if (!ymd) return 0;
  const d = new Date(`${ymd}T23:59:59`);
  return Number.isFinite(d.getTime()) ? d.getTime() : 0;
}
